import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  configurationService,
  CreatePaymentConfigurationData,
  CreateModuleTemplateData,
  CreateBillingCycleData,
} from '../services';

interface ConfigFilters {
  page?: number;
  per_page?: number;
  search?: string;
}

// Payment Configurations
export const usePaymentConfigurations = (filters?: ConfigFilters) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['paymentConfigurations', filters],
    queryFn: async () => {
      const response = await configurationService.getPaymentConfigurations(filters?.page);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreatePaymentConfigurationData) =>
      configurationService.createPaymentConfiguration(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentConfigurations'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreatePaymentConfigurationData> }) =>
      configurationService.updatePaymentConfiguration(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentConfigurations'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => configurationService.deletePaymentConfiguration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentConfigurations'] });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => configurationService.togglePaymentConfigurationStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentConfigurations'] });
    },
  });

  return {
    configurations: data,
    isLoading,
    error,
    createConfiguration: createMutation.mutateAsync,
    updateConfiguration: updateMutation.mutateAsync,
    deleteConfiguration: deleteMutation.mutateAsync,
    toggleStatus: toggleStatusMutation.mutateAsync,
  };
};

export const useActivePaymentMethods = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['paymentConfigurations', 'active'],
    queryFn: async () => {
      const response = await configurationService.getActivePaymentMethods();
      return response.data;
    },
  });

  return {
    paymentMethods: data,
    isLoading,
    error,
  };
};

// Module Templates
export const useModuleTemplates = (filters?: ConfigFilters) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['moduleTemplates', filters],
    queryFn: async () => {
      const response = await configurationService.getModuleTemplates(filters?.page);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateModuleTemplateData) =>
      configurationService.createModuleTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moduleTemplates'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateModuleTemplateData> }) =>
      configurationService.updateModuleTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moduleTemplates'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => configurationService.deleteModuleTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moduleTemplates'] });
    },
  });

  return {
    templates: data,
    isLoading,
    error,
    createTemplate: createMutation.mutateAsync,
    updateTemplate: updateMutation.mutateAsync,
    deleteTemplate: deleteMutation.mutateAsync,
  };
};

export const useTemplatesByType = (typeId: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['moduleTemplates', 'type', typeId],
    queryFn: async () => {
      const response = await configurationService.getTemplatesByType(typeId);
      return response.data;
    },
    enabled: !!typeId,
  });

  return {
    templates: data,
    isLoading,
    error,
  };
};

// Billing Cycles
export const useBillingCycles = (page?: number) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['billingCycles', page],
    queryFn: async () => {
      const response = await configurationService.getBillingCycles(page);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateBillingCycleData) => configurationService.createBillingCycle(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingCycles'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateBillingCycleData> }) =>
      configurationService.updateBillingCycle(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingCycles'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => configurationService.deleteBillingCycle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingCycles'] });
    },
  });

  return {
    cycles: data,
    isLoading,
    error,
    createCycle: createMutation.mutateAsync,
    updateCycle: updateMutation.mutateAsync,
    deleteCycle: deleteMutation.mutateAsync,
  };
};
